import React, { useEffect, useRef, useContext } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import get from 'lodash.get';

import loadScript from 'lib/loadScript';
import { BreakpointContext } from 'lib/ContextTypes';
import placements from 'lib/taboolaConfig';
import MobileDetection from 'lib/MobileDetection';
import MobileApi from 'lib/MobileApi';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { TABOOLA_CUSTOM_PLACEMENT } from 'lib/brandFeatures';
import { setTaboolaLoaded } from 'redux/modules/shared';
import { isBlogArticle } from 'lib/article';
import { VIEW } from 'lib/view';
import { useShouldRemoveTaboolaAds } from 'lib/subscriptionUtils';

import styles from './styles.module.scss';

const verticalWithTaboola = {
  entretenimiento: true,
  msnbc: true,
  news: true,
  noticias: true,
  deportes: true,
  select: true,
  shows: true,
  telemundo: true,
  today: true,
};

const loaderConfig = {
  default: 'nbcnews',
  entretenimiento: 'telemundocom',
  msnbc: 'nbcuniversal-msnbc',
  news: 'nbcnews',
  noticias: 'telemundocom',
  deportes: 'telemundocom',
  select: 'nbcuniversal-network',
  shows: 'telemundocom',
  telemundo: 'telemundocom',
  today: 'nbc-today',
};

const publisherChromelessConfig = {
  today: {
    android: 'nbc-todayappandroid',
    ios: 'nbctodayappios',
  },
  news: {
    android: 'nbcnewsappandroid',
    ios: 'nbcnewsappios',
  },
  mach: {
    android: 'nbcnewsappandroid',
    ios: 'nbcnewsappios',
  },
  better: {
    android: 'nbcnewsappandroid',
    ios: 'nbcnewsappios',
  },
  think: {
    android: 'nbcnewsappandroid',
    ios: 'nbcnewsappios',
  },
  noticias: {
    android: 'telemundocom-app-android',
    ios: 'telemundocom-app-ios',
  },
  deportes: {
    android: 'telemundocom-app-android',
    ios: 'telemundocom-app-ios',
  },
  entretenimiento: {
    android: 'telemundocom-app-android',
    ios: 'telemundocom-app-ios',
  },
  shows: {
    android: 'telemundocom-app-android',
    ios: 'telemundocom-app-ios',
  },
  telemundo: {
    android: 'telemundocom-app-android',
    ios: 'telemundocom-app-ios',
  },
  msnbc: {
    android: 'msnbcappandroid',
    ios: 'msnbcappios',
  },
  default: 'nbcnews-app',
};

/**
 * Maps Redux state to component props
 * @param {object} state - Redux state object
 * @param {object} state.front - Front state
 * @param {object} state.shared - Shared state
 * @param {object} state.article - Article state
 * @returns {object} Mapped props for Taboola component
 */
export const mapStateToProps = ({
  front, shared, article,
}) => ({
  isLiveBlog: isBlogArticle(article?.content?.[0]),
  vertical: shared.vertical,
  pageView: shared.pageView === 'card' ? 'article' : shared.pageView,
  section: shared.section,
  isChromeless: shared.isChromeless,
  loaderLoaded: shared.taboolaLoaderLoaded,
  adsDisabled: front.curation.adsDisabled,
  taboolaFeed: shared.taboolaFeed,
  ecommerceEnabled: article?.content?.[0]?.ecommerceEnabled,
  view: shared.view,
});

// Helper functions extracted for reuse in component and tests
/**
 * Gets the chromeless loader publisher ID based on vertical and device
 * @param {string} vertical - The vertical/brand identifier
 * @returns {string} The publisher ID for chromeless loader
 */
const getChromelessLoaderPublisherID = (vertical) => {
  const device = MobileDetection.Android() ? 'android' : 'ios';

  return publisherChromelessConfig[vertical]
    ? publisherChromelessConfig[vertical][device]
    : publisherChromelessConfig.default;
};

/**
 * Determines if Taboola feed should be shown in chromeless mode
 * @param {string} vertical - The vertical/brand identifier
 * @param {boolean} isChromeless - Whether in chromeless mode
 * @param {boolean} ecommerceEnabled - Whether ecommerce is enabled
 * @returns {boolean} True if Taboola feed should be shown
 */
const shouldShowTaboolaFeedOnChromeless = (vertical, isChromeless, ecommerceEnabled) => {
  const config = {
    msnbc: isChromeless && !ecommerceEnabled,
    news: isChromeless && !ecommerceEnabled,
    telemundo: false,
    today: isChromeless && !ecommerceEnabled,
  };
  return config[vertical];
};

/**
 * Determines the page type from page view and section
 * @param {string} pageView - The current page view
 * @param {string} vertical - The vertical/brand identifier
 * @param {string} section - The page section
 * @returns {string} The page type for Taboola placement
 */
const getTypeFromPageSection = (pageView, vertical, section) => {
  const isTodayCoverABTestPage = (section === 'today-cover-redesign' || section === 'cover');
  if (pageView === 'front' && verticalWithTaboola[vertical]) {
    if (section === '' || isTodayCoverABTestPage) {
      return 'home';
    }
    return 'category';
  }
  if (pageView === 'card') {
    return 'article';
  }
  // ads on showblogs should mimic article
  if (pageView === 'showBlog') {
    return 'article';
  }
  return pageView;
};

/**
 * Determines the breakpoint for Taboola placement
 * @param {string} vertical - The vertical/brand identifier
 * @param {boolean} isChromeless - Whether in chromeless mode
 * @param {boolean} ecommerceEnabled - Whether ecommerce is enabled
 * @param {boolean} isS - Whether in small breakpoint
 * @returns {string} The breakpoint identifier
 */
const getBreakpoint = (vertical, isChromeless, ecommerceEnabled, isS) => {
  if (shouldShowTaboolaFeedOnChromeless(vertical, isChromeless, ecommerceEnabled)) {
    /*
     * When updating hasiOSConfig, make sure the chromeless_feed_ios object
     * exists in src/lib/taboolaConfig.js for the selected brand's page type
     */
    const hasiOSConfig = {
      msnbc: false,
      news: false,
      telemundo: false,
      today: MobileDetection.iOS(),
    };

    return `chromeless_feed${hasiOSConfig[vertical] ? '_ios' : ''}`;
  }

  if (isChromeless) {
    return isS ? 'chromeless_mobile' : 'chromeless_tablet';
  }

  if (isS) {
    return 'mobile';
  }

  return 'desktop';
};

/**
 * Gets a specific Taboola placement configuration
 * @param {string} verticalParam - The vertical/brand identifier
 * @param {string} type - The page type
 * @param {string} breakpoint - The breakpoint identifier
 * @returns {object|null} The placement configuration or null
 */
const getSpecificPlacement = (
  verticalParam,
  type,
  breakpoint,
) => get(placements, [verticalParam, type, breakpoint], null);

/**
 * Gets the Taboola placement configuration based on props and breakpoint
 * @param {object} props - Component props
 * @param {string} props.vertical - The vertical/brand identifier
 * @param {boolean} props.isRail - Whether placement is in rail
 * @param {boolean} props.isMidFront - Whether placement is mid-front
 * @param {boolean} props.isChromeless - Whether in chromeless mode
 * @param {boolean} props.ecommerceEnabled - Whether ecommerce is enabled
 * @param {boolean} props.isLiveBlog - Whether article is live blog
 * @param {string} props.section - Page section
 * @param {string} props.pageView - Page view type
 * @param {object} breakpointContext - Breakpoint context
 * @param {boolean} breakpointContext.isS - Whether in small breakpoint
 * @param {boolean} breakpointContext.isLorXL - Whether in large or XL breakpoint
 * @returns {object|null} The placement configuration or null
 */
export const getPlacement = (props, breakpointContext = {}) => {
  const {
    vertical,
    isRail,
    isMidFront,
    isChromeless,
    ecommerceEnabled,
    isLiveBlog,
    section,
    pageView,
  } = props;
  const { isS = false, isLorXL = true } = breakpointContext;

  const type = getTypeFromPageSection(pageView, vertical, section);

  const isTaboolaCustomPlacement = getFeatureConfigForBrand(
    TABOOLA_CUSTOM_PLACEMENT,
    vertical,
  );

  let breakpoint = getBreakpoint(vertical, isChromeless, ecommerceEnabled, isS);

  if (isRail) {
    breakpoint = isLorXL && !isChromeless ? 'rail' : null;
  }

  if (isTaboolaCustomPlacement) {
    breakpoint = isMidFront ? 'midFront' : null;
  }

  // ecommerce placement
  if (vertical === 'today' && ecommerceEnabled && !isChromeless && !isRail) {
    if (breakpoint === 'mobile') {
      return placements.today.article['commerce-mobile'];
    }
    return placements.today.article.commerce;
  }

  const isArticle = type === 'article';
  if (isArticle && isLiveBlog) {
    return placements.news.liveBlog[breakpoint];
  }

  return getSpecificPlacement(vertical, type, breakpoint);
};

/**
 * Taboola component - Displays content recommendations and native advertising widgets
 * @param {object} props - Component props
 * @param {Function} props.dispatch - Redux dispatch function
 * @param {string} props.vertical - Brand/vertical identifier (news, today, msnbc, etc)
 * @param {string} [props.id='1'] - Unique identifier for placement
 * @param {string} props.pageView - Type of page (article, front, showBlog, etc)
 * @param {string} [props.section=null] - Page section
 * @param {string} [props.canonicalUrl=null] - Canonical URL for article pages
 * @param {boolean} [props.isRail=false] - Whether placement is in sidebar rail
 * @param {boolean} [props.isMidFront=false] - Whether placement is mid-front
 * @param {boolean} props.isChromeless - Whether in app/chromeless mode
 * @param {boolean} [props.isLiveBlog=false] - Whether article is live blog
 * @param {boolean} props.loaderLoaded - Whether Taboola loader script is loaded
 * @param {string|Array} [props.className=null] - Additional CSS classes
 * @param {boolean} [props.adsDisabled=false] - Whether ads are disabled
 * @param {string} [props.pkgClassName='pkg taboola'] - Package CSS class
 * @param {boolean} [props.ecommerceEnabled=false] - Whether ecommerce is enabled
 * @param {string} [props.view=null] - View type (e.g., START_TODAY_APP)
 * @param {boolean} [props.taboolaFeed] - Whether Taboola feed is enabled
 * @returns {JSX.Element|null} Taboola placement container or null if conditions not met
 */
const Taboola = React.memo((props) => {
  const {
    dispatch,
    vertical,
    id = '1',
    pageView,
    section = null,
    canonicalUrl = null,
    isRail = false,
    isChromeless,
    isLiveBlog = false,
    loaderLoaded,
    className: propsClassName = null,
    adsDisabled = false,
    pkgClassName = 'pkg taboola',
    ecommerceEnabled = false,
    view = null,
  } = props;

  const taboolaContainerRef = useRef(null);

  const { isS, isLorXL, hasCheckedViewportSize } = useContext(BreakpointContext);

  const shouldRemoveTaboolaAds = useShouldRemoveTaboolaAds();

  /**
   * Loads the Taboola script if not already loaded
   * @returns {Promise|null} Promise that resolves when script is loaded
   */
  const getLoader = async () => {
    // If ads are disabled or ad-free subscriber, skip loading
    if (adsDisabled || shouldRemoveTaboolaAds) {
      return null;
    }

    if (!loaderLoaded) {
      // Publisher name
      let publisher = isChromeless
        ? getChromelessLoaderPublisherID(vertical)
        : loaderConfig[vertical] || loaderConfig.default;

      // The Today native app does not load in chromeless mode. This is using
      // the MobileApi class to detect if the logic is running inside the
      // native app and assigning the correct loader.

      const isTodayAndroidApp = vertical === 'today'
        && MobileDetection.Android()
        && MobileApi.getAndroidBridge() !== null;

      if (isTodayAndroidApp) {
        publisher = publisherChromelessConfig.today.android;
      }

      // Loader name
      // taboola feed only chromeless only works for news articles and if enabled as query
      // TODO: change this when feed is ready on chromeless today
      let loader = 'loader';
      if (shouldShowTaboolaFeedOnChromeless(vertical, isChromeless, ecommerceEnabled)) {
        loader = 'mobile-loader';
      }
      dispatch(setTaboolaLoaded());

      return loadScript(`//cdn.taboola.com/libtrc/${publisher}/${loader}.js`);
    }
    return null;
  };

  /**
   * Adds Taboola configuration to the window object
   * @returns {void}
   */
  const addConfig = () => {
    const type = getTypeFromPageSection(pageView, vertical, section);

    const pageConfig = type === 'recipe'
      ? { category: 'auto' }
      : { [type]: 'auto' };
    const isArticle = type === 'article';

    /* eslint-disable no-underscore-dangle */

    if (isArticle) {
      if (!isChromeless) {
        let railPlacement = getSpecificPlacement(vertical, type, 'rail');
        if (isLiveBlog) {
          railPlacement = getSpecificPlacement(vertical, 'liveBlog', 'rail');
        }

        if (railPlacement) {
          const railPlacementConfig = { target_type: 'mix', ...railPlacement };
          railPlacementConfig.container = railPlacementConfig.container.replace('%id', id);
          window._taboola.push(railPlacementConfig);
        }
      }

      if (canonicalUrl) {
        pageConfig.url = canonicalUrl;
      }
    }

    if (shouldShowTaboolaFeedOnChromeless(vertical, isChromeless, ecommerceEnabled)) {
      window._taboola.mobile = [];
      window._taboola.mobile.push({
        // run sdkless when testing the js on a browser (no sdk) (optional)
        allow_sdkless_load: true,
        taboola_view_id: new Date().getTime(),
        // override publisher, this value is injected by mobile
        publisher: getChromelessLoaderPublisherID(vertical),
      });
    }

    window._taboola.push(pageConfig);

    if (type !== 'article') {
      window._taboola.push({ flush: true });
    }
  };

  useEffect(() => {
    if (!hasCheckedViewportSize) {
      return;
    }

    /**
     * Initializes Taboola placement and configuration
     * @returns {Promise<void>}
     */
    const initializeTaboola = async () => {
      if (adsDisabled || shouldRemoveTaboolaAds) {
        return;
      }

      const placement = getPlacement(props, { isS, isLorXL });

      if (placement) {
        const placementConfig = { target_type: 'mix', ...placement };
        placementConfig.container = placement.container.replace('%id', id);

        if (taboolaContainerRef.current) {
          taboolaContainerRef.current.id = placementConfig.container;
        }
        // Delay the article rail configuration until the article placement is added.
        if (isRail) {
          return;
        }
        /* eslint-disable no-underscore-dangle */
        window._taboola = window._taboola || [];
        // Push placement config
        window._taboola.push(placementConfig);

        addConfig();

        await getLoader();

        if (window.performance && typeof window.performance.mark === 'function') {
          window.performance.mark('tbl_ic');
        }
        /* eslint-enable no-underscore-dangle */
      }
    };

    initializeTaboola();
  }, [hasCheckedViewportSize]);

  // Note: Remove Taboola tracking when user becomes a subscriber
  useEffect(() => {
    if (shouldRemoveTaboolaAds && typeof window !== 'undefined') {
      /* eslint-disable no-underscore-dangle */
      // Clear the Taboola queue to prevent further tracking
      if (window._taboola) {
        window._taboola = [];
      }
      /* eslint-enable no-underscore-dangle */
    }
  }, [shouldRemoveTaboolaAds]);

  // Hide taboola feed on today page for START_TODAY_APP
  if (view === VIEW.START_TODAY_APP) {
    return null;
  }

  // Do NOT show Taboola ads if adsDisabled or ad-free subscriber
  if (adsDisabled || shouldRemoveTaboolaAds) return null;

  // the same markup should be rendered on the server as well as on the initial
  // render cycle on the client
  return (
    <div
      className={classNames(pkgClassName, styles.taboola, propsClassName, 'dn-print')}
      data-packageid={id}
      data-test="taboola"
      data-testid="taboola"
      data-activity-map={`taboola-${isRail ? 'article-sidebar' : 'article-bottom'}`}
    >
      <div
        data-test="placeholder"
        data-testid="taboola-content"
        ref={taboolaContainerRef}
      />
    </div>
  );
});

Taboola.displayName = 'Taboola';

// unconnected component used for testing
export { Taboola as UnconnectedTaboola };

export default connect(mapStateToProps)(Taboola);
